import "./Buttons.scss";
import { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { BlobProvider } from "@react-pdf/renderer";
import {
  LeaveApplicationPDFRenderer,
  LeaveApplicationPDFTemplateType,
} from "../../context/nonVisual/LeaveApplicationPDF";
import { Button } from "antd";
import { User } from "../../utils/types";
import { Subject } from "rxjs";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { formatDate } from "../../utils/helpers";

export const leavePDF: Subject<string> = new Subject();

type PDFBtnProps = {
  user: User | null;
  workDays: number;
  startDate: Date;
  endDate: Date;
  substitute: User | null;
  userSignature: string;
  autoSignature?: string;
  open: boolean;
  previousYearDaysUsedForRequest: number;
  currentYearDaysUsedForRequest: number;
  requestType?: string;
  unpaidLeaveReason?: string;
};

export const DownloadPDFButton = ({
  user,
  workDays,
  startDate,
  endDate,
  substitute,
  userSignature,
  autoSignature,
  open,
  previousYearDaysUsedForRequest,
  currentYearDaysUsedForRequest,
  requestType,
  unpaidLeaveReason,
}: PDFBtnProps) => {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const [signatureURL, setSignatureURL] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && autoSignature) {
      setSignatureURL(autoSignature);
    }
  }, [open, autoSignature]);

  const saveSignature = () => {
    if (sigCanvasRef.current) {
      const dataUrl = sigCanvasRef.current.toDataURL();
      setSignatureURL(dataUrl);
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      leavePDF.next("");
      setSignatureURL(null);
    }
  };

  const period =
    startDate && endDate
      ? formatDate(startDate) === formatDate(endDate)
        ? formatDate(startDate)
        : `${formatDate(startDate)} — ${formatDate(endDate)}`
      : "---";

  const substituteName = substitute
    ? `${substitute.firstName} ${substitute.lastName}`
    : "----";
  const userName = user ? `${user.firstName} ${user.lastName}` : "---";
  const userTitle = user?.jobTitle || "---";
  const previousYearDaysCount = Number(previousYearDaysUsedForRequest) || 0;
  const currentYearDaysCount = Number(currentYearDaysUsedForRequest) || 0;
  const template: LeaveApplicationPDFTemplateType =
    previousYearDaysCount > 0 && currentYearDaysCount > 0
      ? "mixed-year"
      : "single-year";

  useEffect(() => {
    if (!open) return;

    const raf = requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      const sig = sigCanvasRef.current;
      if (!wrap || !sig) return;

      const rect = wrap.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const canvas = sig.getCanvas();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      sig.clear();
    });

    return () => cancelAnimationFrame(raf);
  }, [open]);

  return (
    <div>
      {autoSignature ? (
        <div className="flex items-center gap-2">
          <img
            src={autoSignature}
            alt="Your saved signature"
            style={{ maxHeight: 60, maxWidth: 200 }}
          />
          <span>Using your saved signature (My Signature)</span>
        </div>
      ) : (
        <>
          <div className="sign-canvas-wrapper" ref={wrapRef}>
            <SignatureCanvas
              penColor="black"
              ref={sigCanvasRef}
              canvasProps={{
                className: "sigCanvas",
              }}
            />
          </div>
          <Button
            disabled={!!signatureURL}
            onClick={saveSignature}
            className="signature-btn"
          >
            Save signature
          </Button>
          <Button onClick={clearSignature} className="signature-btn">
            Clear
          </Button>
        </>
      )}

      {signatureURL && (
        <BlobProvider
          document={
            <LeaveApplicationPDFRenderer
              template={template}
              name={userName}
              position={userTitle}
              days={workDays}
              period={period}
              date={new Date()}
              requestYear={startDate.getFullYear()}
              substitute={substituteName}
              employeeSignature={userSignature}
              managerSignature={signatureURL}
              gender={user?.gender ?? undefined}
              previousYearDaysUsedForRequest={previousYearDaysCount}
              currentYearDaysUsedForRequest={currentYearDaysCount}
              requestType={requestType}
              unpaidLeaveReason={unpaidLeaveReason}
            />
          }
        >
          {({ blob, url, loading }) => {
            if (blob) {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64data = reader.result;
                if (base64data) leavePDF.next(base64data?.toString());
              };
              reader.readAsDataURL(blob);
            }
            return loading ? (
              <span>Generating PDF...</span>
            ) : (
              <>
                {signatureURL && <CheckCircleTwoTone twoToneColor="#52c41a" />}
                <a
                  className="download-request-btn"
                  href={url ?? "/"}
                  download={`Cerere-concediu-${user?.firstName || ""}-${user?.lastName || ""}.pdf`}
                >
                  Download request
                </a>
              </>
            );
          }}
        </BlobProvider>
      )}
    </div>
  );
};
